<template>
  <VueDatePicker
    ref="datePicker"
    :modelValue="selectedDates"
    class="DatePicker"
    autoApply
    arrowNavigation
    :disabled
    :multiCalendars="doubleMonths"
    :timeConfig="{ enableTimePicker: false }"
    :actionRow="{ showSelect: false, showCancel: false, showNow: false, showPreview: true }"
    noToday
    :minDate
    :maxDate
    :locale="dateLocale"
    hideOffsetDates
    preventMinMaxNavigation
    :startDate
    :allowedDates="availableDates"
    :dayNames="weekDayNames"
    :range="range && {
      partialRange: false,
    }"
    :teleport
    :config="{
      monthChangeOnScroll: isMobile
    }"
    :ui="{
      menu: 'DatePickerMenu'
    }"
    data-test-id="date-picker"
    @update:model-value="onDateChange"
    @open="emit('open')"
    @closed="onMenuClosed"
  >
    <template #dp-input="{ isMenuOpen, toggleMenu }">
      <div
        ref="inputGroup"
        class="DatePicker-inputGroup"
        role="button"
        :tabindex="disabled ? -1 : 0"
        aria-haspopup="dialog"
        :aria-expanded="isMenuOpen"
        :aria-label="triggerAriaLabel"
        @keydown.enter.prevent="toggleMenu"
        @keydown.space.prevent="toggleMenu"
        @keydown.down.prevent="toggleMenu"
      >
        <Input
          :label
          :modelValue="startDateDisplay"
          :placeholder="t('ui.booking.search.departure.date.placeholder')"
          :disabled
          readonly
          hideAsterisk
          tabindex="-1"
          class="DatePicker-input"
          v-bind="startField"
        />

        <Transition name="fade" mode="out-in">
          <div v-show="props.range" class="DatePicker-divider" />
        </Transition>

        <Transition name="fade" mode="out-in">
          <Input
            v-if="props.range"
            :modelValue="returnDateDisplay"
            :placeholder="t('ui.booking.search.return.date.placeholder')"
            :disabled
            readonly
            hideAsterisk
            tabindex="-1"
            class="DatePicker-input"
            v-bind="endField"
          />
        </Transition>
      </div>
    </template>

    <template
      #month-year="{
        // @ts-ignore
        month, months, year, instance, handleMonthYearChange, isDisabled
      }"
    >
      <IconButton
        v-if="instance === 0 || !doubleMonths"
        icon="chevron-left"
        :disabled="isDisabled!(false)"
        :ariaLabel="t('ui.booking.search.datePicker.previousmonth')"
        @click="handleMonthYearChange?.(false)"
      />

      <span class="DatePicker-monthYear">{{ months?.[month!]?.text }} {{ year }}</span>

      <IconButton
        v-if="instance === 1 || !doubleMonths"
        icon="chevron-right"
        :disabled="isDisabled!(true)"
        :ariaLabel="t('ui.booking.search.datePicker.nextmonth')"
        @click="handleMonthYearChange?.(true)"
      />
    </template>

    <template #menu-header>
      <h5>{{ t('ui.booking.search.datePicker.header') }}</h5>
    </template>

    <template
      #clear-icon="{ clear }"
    >
      <IconButton
        icon="window-close"
        :size="20"
        ariaLabel="Clear"
        @click="clear"
      />
    </template>

    <template v-if="unlockingDate" #action-extra>
      <DatePickerLegend :unlockingDate="unlockingDate" />
    </template>
  </VueDatePicker>
</template>

<script setup lang="ts">
import { computed, watch, useTemplateRef } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { VueDatePicker } from '@vuepic/vue-datepicker';

import { getWeekdays } from '@cvo/domain/utils/dateUtils';
import { FIRST_DAY_OF_WEEK, type StartOfWeek } from '@cvo/i18n/constants';
import { useDateFormat, useResponsive } from '@cvo/ui/composables';
import type { FieldProps } from '@cvo/ui/composables';

import { isAfter, isBefore, isSameDay, getDefaultOptions } from 'date-fns';
import Input, { type InputProps } from '../../atoms/Input/Input.vue';
import IconButton from '../../atoms/IconButton/IconButton.vue';
import DatePickerLegend from './DatePickerLegend.vue';

interface CalenderPickerProps extends FieldProps {
  availableDates?: Array<Date>,
  range?: boolean,
  unlockingDate?: Date,
  maxDate?: Date,
  minDate?: Date,
  hideAsterisk?: boolean,
  startField?: InputProps,
  endField?: InputProps,
  allowSameDayRange?: boolean;
  teleport?: string;
  /** When true and no date is selected, inputs display "Anytime" instead of the placeholder */
  anytime?: boolean;
}

/** Single or Dual dates depending if `range` mode is enabled or not */
export type DatesSelection = null | Date | [Date, Date];

const props = withDefaults(defineProps<CalenderPickerProps>(), {
  range: false,
  required: false,
  error: undefined,
  minDate: () => new Date(),
});

const emit = defineEmits<{
  /** The calendar menu opened. */
  open: [];
  /** The calendar menu closed. */
  closed: [];
}>();

const selectedDates = defineModel<DatesSelection>({
  required: true,
});

const { locale, t } = useI18n();
const { isMobile, isTabletOrDesktop } = useResponsive();
const datePicker = useTemplateRef('datePicker');
const inputGroup = useTemplateRef<HTMLElement>('inputGroup');

const dateLocale = computed(() => {
  return getDefaultOptions().locale;
});

// If the min/max change and the selected date is invalid, we clear the selection
watch(() => [props.minDate, props.maxDate], ([newMin, newMax]) => {
  function isSelectedDateInvalid(date: Date | null) {
    if (!date) return false;

    const isBeforeMin = newMin ? isBefore(date, newMin) : false;
    const isAfterMax = newMax ? isAfter(date, newMax) : false;

    return isBeforeMin || isAfterMax;
  }

  const hasInvalidDate = [selectedDates.value].flat()?.some(isSelectedDateInvalid);

  if (hasInvalidDate) {
    selectedDates.value = null;
  }
});

// When range mode switches, we either reset the selected dates or pre-selected the departure date as the start of the range (if enabled)
watch(() => props.range, () => {
  const departureDate = Array.isArray(selectedDates.value) ? selectedDates.value[0] : selectedDates.value;

  if (!departureDate) {
    selectedDates.value = null;
    return;
  }

  // @ts-expect-error Only case where we allow a partial range.
  selectedDates.value = props.range ? [departureDate, null] : departureDate;
});

// ===== VueDatePicker props ===== //

const firstDayOfWeek = computed<StartOfWeek>(() => {
  return FIRST_DAY_OF_WEEK[locale.value] ?? 0; // TODO Use a prop instead. And remove FIRST_DAY_OF_WEEK constant to use per locale attribute in tenant.config instead.
});

const doubleMonths = computed(() => isTabletOrDesktop.value);

const startDate = computed(() => {
  if (selectedDates.value) return undefined;
  return props.minDate;
});

const weekDayNames = computed(() => {
  return getWeekdays(locale.value, firstDayOfWeek.value);
});

// ============================== //

const firstDate = computed<Date | null>(() => (Array.isArray(selectedDates.value) ? selectedDates.value[0] : selectedDates.value as Date | null));
const formattedStartDate = useDateFormat(firstDate);
const startDateDisplay = computed(() => (props.anytime && !firstDate.value ? t('ui.booking.search.departure.date.anytime') : formattedStartDate.value));

const secondDate = computed<Date | null>(() => (Array.isArray(selectedDates.value) ? selectedDates.value[1] : null));
const formattedReturnDate = useDateFormat(secondDate);
const returnDateDisplay = computed(() => (props.anytime && !secondDate.value ? t('ui.booking.search.departure.date.anytime') : formattedReturnDate.value));

// role="button" makes the group's children presentational, so the accessible name is built here.
const triggerAriaLabel = computed(() => {
  const fields: Array<[label: string | undefined, value: string]> = [[props.startField?.label ?? props.label, startDateDisplay.value]];

  if (props.range) fields.push([props.endField?.label, returnDateDisplay.value]);

  return fields
    .filter(([label]) => label)
    .map(([label, value]) => (value ? `${label}: ${value}` : label))
    .join(', ');
});

// Requiring [tabindex] skips offset days from neighbouring months, which hideOffsetDates leaves without one.
const FOCUS_TARGETS = [
  '[data-dp-element-active][tabindex]', // the selected day
  '[data-dp-action-element][tabindex]:not([aria-disabled])', // the first selectable day
  '[data-dp-action-element][tabindex]', // nothing selectable: settle for any real day
];

// VueDatePicker's own focus-on-open targets the first day cell, which is often unfocusable
// (see FOCUS_TARGETS), leaving focus stuck on the trigger and arrow keys inert. We focus a
// real day ourselves once the menu mounts.
watch(() => datePicker.value?.dpMenuRef?.()?.value?.$el as HTMLElement | undefined, (menu) => {
  if (!menu) return;

  for (const selector of FOCUS_TARGETS) {
    const day = menu.querySelector<HTMLElement>(selector);

    if (day) {
      day.focus();
      break;
    }
  }
}, { flush: 'post' });

// Escape leaves focus on nothing otherwise; click-outside already refocuses on its own before this runs.
function onMenuClosed() {
  inputGroup.value?.focus();
  emit('closed');
}

// ===== Date selection strategies ===== //

const ALL_DATES_SELECTED = 2;
function onDateChange(newDates: DatesSelection) {
  const selection = sameDayRangeStrategy(newDates);
  selectedDates.value = selection;
}

/**
 * Return the dates selection respecting the "same day range" strategy.
 * If enabled, the selection allows for same day as start and end of range.
 * If disabled, the selection resets to null when start and end of range are the same day.
 * @param newDates Selected date(s)
 * @returns The dates selection respecting the "same day range" strategy
 */
function sameDayRangeStrategy(newDates: DatesSelection): DatesSelection {
  if (!props.allowSameDayRange && Array.isArray(newDates)) {
    const isFullRangeSelected = newDates.length === ALL_DATES_SELECTED;
    const hasSelectedSameDate = isSameDay(newDates[0], newDates[1]);

    if (isFullRangeSelected && hasSelectedSameDate) {
      return null;
    }
  }
  return newDates;
}

// ===================================== //

defineExpose({
  openMenu: () => datePicker.value?.openMenu(),
  closeMenu: () => datePicker.value?.closeMenu(),
});
</script>

<style lang="scss">
@use 'sass:math';
@use '@vuepic/vue-datepicker/dist/main.css';
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';

// Do not include in cvo-ui layer: these must override vue-datepicker's own unlayered
// main.css defaults, and unlayered CSS always beats layered CSS regardless of source order.
:root {
  --dp-cell-size: #{32px + math.max(vars.$molecules-datepicker-base-padding-top-bottom, vars.$molecules-datepicker-base-padding-left-right)};
  --dp-cell-padding: #{vars.$molecules-datepicker-base-padding-top-bottom} #{vars.$molecules-datepicker-base-padding-left-right};
  --dp-border-radius: #{vars.$molecules-datepicker-datepicker-border-radius};
  --dp-cell-border-radius: #{vars.$molecules-datepicker-base-day-border-radius};
  --dp-menu-padding: #{vars.$molecules-datepicker-base-padding-top-bottom};
  --dp-menu-min-width: unset;
  --dp-font-family: #{vars.$font-family-body};
}

// Do not include in cvo-ui layer. It would have less priority than the component's default style.

// DatePicker Input
.DatePicker {
  --date-picker-input-rt-gap: #{vars.$dimensions-d-3};

  &-inputGroup {
    display: flex;
    gap: var(--date-picker-input-rt-gap);
    align-items: stretch;

    &:focus-visible {
      outline: 2px solid vars.$color-brand-focus;
      outline-offset: 2px;
    }

    @container (max-width: #{vars.$screen-sm-max-width}) {
      flex-direction: column;
    }
  }

  &-input {
    flex: 1;
    min-width: 0;
  }

  .dp--clear-btn {
    color: vars.$color-neutral-gray-700;
    &:hover {
      color: vars.$color-neutral-gray-800;
    }
  }
}

// DatePicker popup
.DatePickerMenu {
  // Custom styles for the VueDatePicker component
  &.dp--theme-light {
    --dp-primary-color: #{vars.$molecules-datepicker-base-day-selected-bg-color};
    --dp-primary-text-color: #{vars.$molecules-datepicker-base-day-selected-text-color};
    --dp-range-between-dates-background-color: var(--dp-primary-color);
    --dp-range-between-dates-text-color: var(--dp-primary-text-color);
    --dp-hover-color: #{vars.$molecules-datepicker-base-day-hover-bg-color};
    --dp-disabled-background-color: #{vars.$molecules-datepicker-base-day-disabled-bg-color};
    --dp-border-color: #{vars.$molecules-datepicker-datepicker-border-color};
    --dp-background-color: #{vars.$molecules-datepicker-datepicker-bg-color};
    --dp-menu-border-color: #{vars.$molecules-datepicker-datepicker-border-color};
    --dp-text-color: #{vars.$molecules-datepicker-base-day-default-text-color};

    --dp-border-radius: #{vars.$molecules-datepicker-datepicker-border-radius};
    --dp-border-stroke: #{vars.$molecules-datepicker-datepicker-border-stroke};
  }

  &.dp--theme-dark {
    --dp-disabled-background-color: #{vars.$color-neutral-gray-900};
  }

  &.dp--menu {
    box-shadow: vars.$shadow-md;
    border-width: var(--dp-border-stroke);

    // Main title
    .dp--menu-header {
      padding: var(--dp-menu-padding) 0;
      margin: 0 var(--dp-menu-padding);
      border-bottom: vars.$molecules-datepicker-base-border-stroke solid var(--dp-border-color);
      @include text.heading('h5');
      color: vars.$molecules-datepicker-datepicker-title-color;
      h5 {
        color: inherit;
      }
    }

    .dp--month-year-wrap {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      justify-content: space-between;
      align-items: center;
      @include text.heading('h6');
      color: vars.$molecules-datepicker-base-monthyear-default-title-color;

      @include responsive.tablet {
        .DatePicker-monthYear {
          grid-column: 2;
        }
      }

      button {
        &:not(:disabled) {
          color: vars.$molecules-datepicker-base-monthyear-default-icon-color;
        }

        // Left button monthYear -> aligned to the left
        &:first-child {
          justify-self: start;
        }
        // monthYear and right button -> aligned to the right
        &:last-child {
          justify-self: end;
        }
      }

    }

    @include responsive.mobile {
      .dp--calendar-next {
        margin: 0;
      }
    }

    // remove horizontal line separator between weekday header and the calendar
    .dp--calendar-header-separator {
      display: none;
    }

    // weekday styles
    .dp--calendar-header-item {
      color: vars.$molecules-datepicker-base-weekday-default-text-color;
      border-radius: vars.$molecules-datepicker-base-weekday-border-radius;
      background-color: vars.$molecules-datepicker-base-weekday-default-bg-color;
      font-size: 0.75rem;
      font-weight: vars.$typography-weight-body;
      line-height: 2;
      height: 32px;
      padding: var(--dp-cell-padding) 0;
    }

    // Legend slot
    .dp--action-extra {
      display: inline-flex;
      align-items: center;
      gap: vars.$dimensions-d-2;
      width: calc(100% - var(--dp-menu-padding) * 2);
      padding: var(--dp-menu-padding) 0;
      margin: 0 var(--dp-menu-padding);

      text-align: revert;
      color: vars.$molecules-datepicker-datepicker-secondary-text-color;
      @include text.text(sm);

      border-top: vars.$molecules-datepicker-base-border-stroke solid var(--dp-border-color);
    }


    .dp--arrow-top,
    .dp--arrow-bottom {
      border-width: var(--dp-border-stroke);
    }

    .dp--arrow-top {
      top: 0; // Fix issue arrow is under the header because of DOM order.
    }

    .dp--cell-inner {
      border: vars.$molecules-datepicker-base-day-border-stroke solid vars.$molecules-datepicker-base-day-default-border-color;
      background-color: vars.$molecules-datepicker-base-day-default-bg-color;
      // Borders and background color for selection states
      &.dp--active,
      &.dp--range-start,
      &.dp--range-end,
      &.dp--range-between,
      &.dp--date-hoverable-start,
      &.dp--date-hoverable-end {
        border-color: vars.$molecules-datepicker-base-day-selected-border-color;
      }

      &.dp--active {
        border-width: math.div(vars.$molecules-datepicker-base-day-selected-border-stroke, 2) vars.$molecules-datepicker-base-day-selected-border-stroke;
      }
      &.dp--cell-disabled {
        background-color: vars.$molecules-datepicker-base-day-disabled-bg-color;
        border-color: vars.$molecules-datepicker-base-day-disabled-border-color;
        color: vars.$molecules-datepicker-base-day-disabled-text-color;
      }
      &.dp--range-border-start, &.dp--date-hoverable-start {
        border-width: math.div(vars.$molecules-datepicker-base-day-selected-border-stroke, 2) 0 math.div(vars.$molecules-datepicker-base-day-selected-border-stroke, 2) vars.$molecules-datepicker-base-day-selected-border-stroke;
      }
      &.dp--range-border-end, &.dp--date-hoverable-end {
        border-width: math.div(vars.$molecules-datepicker-base-day-selected-border-stroke, 2) vars.$molecules-datepicker-base-day-selected-border-stroke math.div(vars.$molecules-datepicker-base-day-selected-border-stroke, 2) 0px;
      }
      &.dp--range-between {
        border-width: math.div(vars.$molecules-datepicker-base-day-selected-border-stroke, 2) 0;
      }

      &:hover {
        &:not(.dp--range-end):not(.dp--date-hoverable-end) {
          border-color: vars.$molecules-datepicker-base-day-hover-border-color;
        }
        color: vars.$molecules-datepicker-base-day-hover-text-color;
        background-color: vars.$molecules-datepicker-base-day-hover-bg-color;
        transition: background vars.$transition-fast ease-in-out,
          border-color vars.$transition-fast ease-in-out,
          color vars.$transition-fast ease-in-out;
      }

      &.dp--date-hoverable-end {
        &:not(.dp--cell-disabled) {
          color: var(--dp-primary-text-color);
        }
        &.dp--cell-disabled {
          color: var(--dp-secondary-color);
        }
      }
    }
  }
}
</style>
